/**
 * Sections arrive as you reach them, once.
 *
 * One IntersectionObserver for the whole page rather than one per section, and
 * nothing at all when the visitor has asked for reduced motion or the browser
 * has no observer - in both cases every section is simply already there.
 */

import { useEffect } from "react";

const REVEAL_SELECTOR = "[data-reveal]";

export function useReveal(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const doc = globalThis.document;
    if (!doc) return;

    const reduced = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const nodes = Array.from(doc.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
    if (reduced || typeof IntersectionObserver === "undefined") {
      for (const node of nodes) node.dataset.reveal = "in";
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.reveal = "in";
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [active]);
}
